/**
 * SRO Sticky ATC — custom variant select
 * =========================================
 * Progressively replaces the native <select> inside the sticky ATC bar with a
 * fully-branded, CSS-controlled listbox panel so the open state matches SRO
 * visual standards instead of rendering the OS-native dropdown menu.
 *
 * The native <select> stays in the DOM (visually hidden, aria-hidden="true")
 * because:
 *   - Shopify's product-form.js reads its `.value` for the add-to-cart request
 *   - Hyper's sticky-atc-bar.js watches for `change` events on it to update
 *     the displayed price and available-state
 *
 * We dispatch a native `change` event every time we update `.selectedIndex`
 * so both of the above continue to work without modification.
 *
 * ARIA: The trigger button is role="combobox" with aria-haspopup="listbox" and
 * aria-expanded. The panel is role="listbox". Each item is role="option" with
 * aria-selected. Keyboard: Enter/Space open; Arrow keys navigate; Enter/Space
 * select; Escape closes.
 *
 * Author  : RevShift Media · hello@revshiftmedia.com
 * Project : Stay Ready Outdoors (c5e15f-6e.myshopify.com)
 * Created : 2026-04-18
 */
(function () {
  'use strict';

  /* -------------------------------------------------------------------------
   * Build one custom select for a given native <select> element.
   * Idempotent — exits immediately if already initialised.
   * ----------------------------------------------------------------------- */
  function buildCustomSelect(nativeSelect) {
    if (nativeSelect.dataset.sroSelectInit) return;
    nativeSelect.dataset.sroSelectInit = '1';

    const wrapper = nativeSelect.closest('.select');
    if (!wrapper) return;

    // ------------------------------------------------------------------
    // 1. Hide the native <select> from view while keeping it in the DOM
    // ------------------------------------------------------------------
    nativeSelect.style.cssText = [
      'position: absolute',
      'opacity: 0',
      'pointer-events: none',
      'width: 1px',
      'height: 1px',
      'overflow: hidden',
      'clip: rect(0 0 0 0)',
      'white-space: nowrap',
    ].join('; ');
    nativeSelect.setAttribute('aria-hidden', 'true');
    nativeSelect.setAttribute('tabindex', '-1');

    // ------------------------------------------------------------------
    // 2. Build trigger button (the visible "closed state")
    // ------------------------------------------------------------------
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'sro-select__trigger';
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', nativeSelect.getAttribute('aria-label') || 'Select a variant');

    const label = document.createElement('span');
    label.className = 'sro-select__trigger-label';

    const caret = document.createElement('span');
    caret.className = 'sro-select__trigger-caret';
    caret.setAttribute('aria-hidden', 'true');
    caret.innerHTML =
      '<svg viewBox="0 0 12 7" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M1 1l5 5 5-5"/></svg>';

    trigger.appendChild(label);
    trigger.appendChild(caret);

    // ------------------------------------------------------------------
    // 3. Build listbox panel
    // ------------------------------------------------------------------
    const panel = document.createElement('ul');
    panel.className = 'sro-select__panel';
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;

    /* Build (or rebuild) option <li> elements from current native options */
    function buildOptions() {
      panel.innerHTML = '';
      Array.from(nativeSelect.options).forEach(function (opt, i) {
        const li = document.createElement('li');
        li.className = 'sro-select__option';
        li.setAttribute('role', 'option');
        li.dataset.value = opt.value;
        li.dataset.index = String(i);
        li.textContent = opt.text;
        const isSel = i === nativeSelect.selectedIndex;
        li.setAttribute('aria-selected', String(isSel));
        if (isSel) li.classList.add('is-selected');
        // mousedown so the blur on trigger doesn't fire before click registers
        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          selectOption(i);
        });
        panel.appendChild(li);
      });
    }

    /* Sync trigger label to the currently selected option */
    function syncLabel() {
      const opt = nativeSelect.options[nativeSelect.selectedIndex];
      label.textContent = opt ? opt.text : '';
    }

    buildOptions();
    syncLabel();

    // ------------------------------------------------------------------
    // 4. Open / close helpers
    // ------------------------------------------------------------------
    function openPanel() {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      // Scroll active item into view
      const active = panel.querySelector('.is-selected');
      if (active) active.scrollIntoView({ block: 'nearest' });
    }

    function closePanel() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }

    var activeIndex = -1; // keyboard cursor

    // ------------------------------------------------------------------
    // 5. Select an option by index
    // ------------------------------------------------------------------
    function selectOption(index) {
      if (index < 0 || index >= nativeSelect.options.length) return;
      nativeSelect.selectedIndex = index;
      const ev = new Event('change', { bubbles: true });
      nativeSelect.dispatchEvent(ev);
      syncLabel();
      // Update aria-selected on all options
      panel.querySelectorAll('.sro-select__option').forEach(function (li, i) {
        const sel = i === index;
        li.setAttribute('aria-selected', String(sel));
        li.classList.toggle('is-selected', sel);
      });
      closePanel();
      trigger.focus();
    }

    // ------------------------------------------------------------------
    // 6. Event listeners
    // ------------------------------------------------------------------

    // Toggle on trigger click
    trigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (panel.hidden) {
        openPanel();
      } else {
        closePanel();
      }
    });

    // Keyboard navigation on trigger
    trigger.addEventListener('keydown', function (e) {
      const opts = panel.querySelectorAll('.sro-select__option');
      const total = opts.length;

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (panel.hidden) {
          openPanel();
        } else if (activeIndex >= 0) {
          selectOption(activeIndex);
        }
      } else if (e.key === 'Escape') {
        closePanel();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (panel.hidden) openPanel();
        activeIndex = Math.min(activeIndex + 1, total - 1);
        highlightActive(opts);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (panel.hidden) openPanel();
        activeIndex = Math.max(activeIndex - 1, 0);
        highlightActive(opts);
      } else if (e.key === 'Tab') {
        closePanel();
      }
    });

    function highlightActive(opts) {
      opts.forEach(function (li, i) {
        if (i === activeIndex) {
          li.classList.add('is-active');
          li.scrollIntoView({ block: 'nearest' });
        } else {
          li.classList.remove('is-active');
        }
      });
    }

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (!wrapper.contains(e.target)) closePanel();
    });

    // Sync trigger label if Hyper's JS updates the native select externally
    // (e.g. the main product form variant picker changes the active variant
    // and sticky-atc-bar.js syncs the bar's <select> to match)
    let suppressExternalSync = false;
    nativeSelect.addEventListener('change', function () {
      if (suppressExternalSync) return;
      syncLabel();
      buildOptions();
    });

    // ------------------------------------------------------------------
    // 7. Insert into DOM
    //    Hyper's caret SVG is a sibling of the native <select> inside
    //    .select — hide it since our trigger has its own caret.
    // ------------------------------------------------------------------
    const hyperCaret = wrapper.querySelector('svg, .icon-caret');
    if (hyperCaret) hyperCaret.style.display = 'none';

    wrapper.appendChild(trigger);
    wrapper.appendChild(panel);
  }

  /* -------------------------------------------------------------------------
   * Init: run on DOMContentLoaded + watch for sticky bar appearing in DOM
   * (Hyper slides the bar in via JS; the element is in the DOM from page load
   * but may not be populated yet when this script first runs).
   * ----------------------------------------------------------------------- */
  function initAll() {
    document
      .querySelectorAll(
        '.sticky-atc-bar__variant-select:not([data-sro-select-init])'
      )
      .forEach(buildCustomSelect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Watch for the sticky bar custom element upgrading / options populating
  const observer = new MutationObserver(initAll);
  observer.observe(document.body, { childList: true, subtree: true });
})();
